/**
 * LEGACY remnants of the pre-refresh Knowledge Database, kept only for:
 * - historical comment citation chips (`knowledgeDataRecordId` on ticket
 *   comments) — the fetch 404s now that the old routes are gone and the
 *   "Source was deleted" fallback renders, but the schema/type is still
 *   referenced by the chip components and `Preview`.
 * - the read-only enterprise-memories DTO (the Memories tab).
 *
 * Everything else (models, records CRUD, spreadsheet import) was removed
 * with the knowledge refresh — see utils/parsers/knowledge.ts for the new
 * sources/rules/activity types.
 */
import { isMongoId } from "validator";
import { z } from "zod";

import { withApiAttributes } from "./common";

export const knowledgeDataRecordPropertySchema = z.object({
  _id: z.string().refine(isMongoId),
  name: z.string().min(1),
  value: z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.undefined(),
  ]),
  propertyId: z.string().refine(isMongoId),
});
export type KnowledgeDataRecordProperty = z.infer<
  typeof knowledgeDataRecordPropertySchema
>;

export const knowledgeDataRecordSchema = z
  .object({
    modelId: z.string().refine(isMongoId),
    data: z.array(knowledgeDataRecordPropertySchema),
  })
  .merge(withApiAttributes);
export type KnowledgeDataRecord = z.infer<typeof knowledgeDataRecordSchema>;

/** Response shape of the RETIRED GET /knowledgeDataRecord/:id route. The chip
 * components still parse cached/error payloads through this. */
export const getKnowledgeDataRecordResponseSchema = z
  .object({
    _id: z.string(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deletedAt: z.string().datetime().optional(),
    data: z.array(knowledgeDataRecordPropertySchema),
    modelId: z.string(),
  })
  .passthrough();
export type GetKnowledgeDataRecordResponse = z.infer<
  typeof getKnowledgeDataRecordResponseSchema
>;

export function isKnowledgeDataRecordPropertyEmpty(
  field: Pick<KnowledgeDataRecordProperty, "value">,
): boolean {
  const { value } = field;
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  return false;
}

///// Enterprise memories (read-only AI-generated knowledge)

// GET /v1/private/catalyst/knowledge/memories
//
// NOTE: deliberately does NOT use withApiAttributes — the DTO uses `id` (not
// `_id`), and `occurredAt` is nullable. The christo handler normalizes the
// date strings so `.datetime()` validation is safe here.
export const enterpriseMemoryScopeSchema = z.object({
  brand: z.string().nullable(),
  region: z.string().nullable(),
  team: z.string().nullable(),
  projectId: z.string().nullable(),
});
export type EnterpriseMemoryScope = z.infer<typeof enterpriseMemoryScopeSchema>;

// Where a fact waiting in warhol's Unresolved Queue stands (christo
// `memoryQueueStatus`, spec surbhi.kapoor/0089). Rendered as words, never the slug.
export const memoryQueueStatusSchema = z.enum([
  "waiting",
  "held",
  "checked",
  "merged",
  "recycled",
  "escalated",
]);
export type MemoryQueueStatus = z.infer<typeof memoryQueueStatusSchema>;

export const enterpriseMemorySchema = z.object({
  id: z.string(),
  type: z.string(),
  content: z.string(),
  context: z.string().nullable(),
  scope: enterpriseMemoryScopeSchema,
  confirmationCount: z.number(),
  occurredAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime(),
  lane: z.enum(["memory", "queue"]).optional(),
  status: memoryQueueStatusSchema.nullish(),
  repeatCount: z.number().optional(),
  ageDays: z.number().optional(),
  // Why warhol's memory lane did what it did to a memory row, in words (spec surbhi.kapoor/0086).
  reason: z.string().nullish(),
  // Each statement the memory lane counted on the row, newest last.
  merges: z
    .array(
      z.object({
        at: z.string().datetime().nullable(),
        by: z.string(),
        reason: z.string(),
      }),
    )
    .optional(),
});
export type EnterpriseMemory = z.infer<typeof enterpriseMemorySchema>;

export const enterpriseMemoryTypeCountSchema = z.object({
  type: z.string(),
  count: z.number(),
});
export type EnterpriseMemoryTypeCount = z.infer<
  typeof enterpriseMemoryTypeCountSchema
>;

export const getEnterpriseMemoriesResponseSchema = z.object({
  memories: z.array(enterpriseMemorySchema),
  typeCounts: z.array(enterpriseMemoryTypeCountSchema),
  more: z.boolean(),
  nextCursor: z.string().nullable(),
});
export type GetEnterpriseMemoriesResponse = z.infer<
  typeof getEnterpriseMemoriesResponseSchema
>;
